/**
 * Thin fetch wrapper for the Hono Workers API.
 *
 * - Base URL 來自 VITE_API_URL（.env），dev 時也可透過 vite proxy 走 `/api` 相對路徑。
 * - 一律送 cookie（HttpOnly session）→ credentials: 'include'。
 * - 拋出結構化 ApiError，呼叫端可以 instanceof 判斷。
 *
 * 在 demo 模式下，後端可能還沒跑：
 * - 呼叫端可以用 try/catch + ApiError 自行判斷
 * - 也可以使用 `apiSafe.*` 去拿 `{ data, error }` shape，避免 throw 白屏
 */

import type { Persona, Theme } from './personas';

function resolveBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
  if (typeof window === 'undefined') return configured;
  const host = window.location.hostname;
  if (host === 'oracle.yourspce.org' || host === 'oracle-lab.yourspce.org') {
    return '';
  }
  return configured;
}

const BASE_URL = resolveBaseUrl();

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  const res = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      ...(headers ?? {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      payload = await res.text();
    }
    throw new ApiError(res.status, `API ${res.status} ${res.statusText}`, payload);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('application/json') ? await res.json() : ((await res.text()) as unknown)) as T;
}

export const apiClient = {
  get: <T = unknown>(path: string, init?: RequestOptions) => api<T>(path, { ...init, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, init?: RequestOptions) =>
    api<T>(path, { ...init, method: 'POST', body }),
  patch: <T = unknown>(path: string, body?: unknown, init?: RequestOptions) =>
    api<T>(path, { ...init, method: 'PATCH', body }),
  delete: <T = unknown>(path: string, init?: RequestOptions) => api<T>(path, { ...init, method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// 型別 — 對齊 docs/api-contract.md
// ---------------------------------------------------------------------------

export type Locale = 'en-US' | 'zh-TW' | 'fr-CA' | 'es-MX';

export type SessionPhase = 1 | 2 | 3 | 4 | 5;

export interface SessionUser {
  id: string;
  email: string;
  locale: Locale;
}

export interface MessageRecord {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  cards_drawn?: string[];
  crisis?: CrisisSignal | null;
  side_context?: SideContext | null;
  created_at: number;
}

export interface CrisisSignal {
  level?: 'none' | 'low' | 'medium' | 'high' | string;
  type?: string;
  title?: string;
  message?: string;
  rounds_counted?: boolean;
  resources?: Array<{ label?: string; value?: string; href?: string; phone?: string }>;
}

export interface SideContext {
  type?: string;
  reason?: string;
  rounds_counted?: boolean;
  summary?: string;
  chapter?: string;
  signals?: string[];
  choices?: string[];
  [key: string]: unknown;
}

export interface SessionRecord {
  id: string;
  persona: Persona;
  theme: Theme;
  locale: Locale;
  phase: SessionPhase;
  rounds_used: number;
  is_paid: boolean;
  messages?: MessageRecord[];
  created_at: number;
}

export interface CalibrateResponse {
  recommended: Persona;
  scores: Record<Persona, number>;
  reasoning_key: string;
}

export interface TurnResponse {
  message: MessageRecord;
  session: Pick<SessionRecord, 'rounds_used' | 'phase' | 'is_paid'>;
  paywall_triggered?: boolean;
  crisis?: CrisisSignal | null;
  side_context?: SideContext | null;
  side_input?: SideContext | null;
}

export type SpreadPosition = 'past' | 'present' | 'future';

export interface SpreadCardReading {
  slug: string;
  name: string;
  position: SpreadPosition;
  position_label: string;
  short_reading: string;
}

export interface SpreadResponse {
  spread: 'past_present_future';
  cards: SpreadCardReading[];
  overall: string;
  next_question: string;
  message: MessageRecord;
  crisis?: CrisisSignal | null;
  side_context?: SideContext | null;
}

export interface PayResponse {
  success: boolean;
  payment_id: string;
  session: Pick<SessionRecord, 'is_paid' | 'phase' | 'rounds_used'>;
}

export interface CardSummary {
  slug: string;
  position: string;
  interpretation: string;
}

export interface ReportResponse {
  generated_at: number;
  summary_markdown: string;
  cards_summary: CardSummary[];
  key_insights: string[];
  pdf_url: string;
}

// ---------------------------------------------------------------------------
// Type-safe 高階 API
// ---------------------------------------------------------------------------

export const tarotApi = {
  mockLogin: (email?: string) =>
    apiClient.post<{ user: SessionUser }>('/api/auth/mock-login', email ? { email } : {}),

  me: () => apiClient.get<{ user: SessionUser }>('/api/auth/me'),

  logout: () => apiClient.post<void>('/api/auth/logout'),

  createSession: (input: {
    persona: Persona;
    theme: Theme;
    locale: Locale;
    consent_version: 'v1';
  }) => apiClient.post<SessionRecord>('/api/sessions', input),

  calibrate: (answers: Array<'a' | 'b' | 'c'>) =>
    apiClient.post<CalibrateResponse>('/api/sessions/calibrate', { answers }),

  getSession: (id: string) => apiClient.get<SessionRecord>(`/api/sessions/${id}`),

  sendTurn: (id: string, content: string) =>
    apiClient.post<TurnResponse>(`/api/sessions/${id}/turn`, { content }),

  createSpread: (id: string, input: {
    spread: 'past_present_future';
    picks: number[];
    concern?: string;
  }) => apiClient.post<SpreadResponse>(`/api/sessions/${id}/spread`, input),

  pay: (id: string) => apiClient.post<PayResponse>(`/api/sessions/${id}/pay`, {}),

  getReport: (id: string, options?: { mock?: boolean }) =>
    apiClient.get<ReportResponse>(`/api/sessions/${id}/report${options?.mock ? '?mock=1' : ''}`),
};
